import { Alert, Button, Card, CardContent, Field, Input } from '@lastrouter/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ChevronRight, LogIn } from '@lastrouter/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, API_BASE, ApiError, setCsrfToken } from '../api/client.js';
import { errorMessage, queryKey } from '../api/hooks.js';
import { useBrand } from '../app/shell.js';

export function LoginPage() {
  const { t } = useTranslation();
  const brand = useBrand();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function devLogin(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await api('devLogin', { body: { token: token.trim() } });
      setCsrfToken(session.csrf_token);
      qc.setQueryData(queryKey('session'), session);
      await navigate({ to: session.is_admin ? '/' : '/clients' });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? t('login.refused') : errorMessage(t, err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-page px-4">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <div className="flex flex-col items-center gap-3 text-center">
          {brand?.logo ? <img src={brand.logo} alt="" className="h-10 w-auto" /> : null}
          <h1 className="text-[length:var(--lr-text-xl)] font-semibold text-fg">{t('login.title')}</h1>
          <p className="text-fg-secondary">{brand?.assistant_name ? `${brand.assistant_name}. ` : ''}{t('login.subtitle')}</p>
        </div>
        <Card>
          <CardContent className="flex flex-col gap-4">
            <Button variant="primary" asChild>
              {/* Navegação real: o serviço redireciona para o IdP e volta com o cookie de sessão. */}
              <a href={`${API_BASE}/auth/login`}>
                <LogIn />
                {t('login.sso')}
              </a>
            </Button>
            <details className="group">
              <summary className="flex cursor-pointer list-none items-center gap-1 rounded-[var(--lr-radius-sm)] text-[length:var(--lr-text-sm)] text-fg-secondary hover:text-fg focus-visible:outline-2 focus-visible:outline-focus">
                <ChevronRight className="size-4 transition-transform group-open:rotate-90" aria-hidden="true" />
                {t('login.dev')}
              </summary>
              <form className="mt-3 flex flex-col gap-3" onSubmit={devLogin}>
                <Field id="token-dev" label={t('login.token')} hint={t('login.devHelp')} error={error ?? undefined}>
                  <Input value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" spellCheck={false} required minLength={10} />
                </Field>
                <Button type="submit" disabled={busy || token.trim().length < 10}>
                  {t('login.submit')}
                </Button>
              </form>
            </details>
          </CardContent>
        </Card>
        {error && !token ? <Alert tone="danger" title={error} /> : null}
        <p className="text-center text-[length:var(--lr-text-xs)] text-fg-muted">{t('login.footer')}</p>
      </div>
    </main>
  );
}
